
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchInput } from "@/components/social-groups/SearchInput";
import { SocialGroupsList } from "@/components/social-groups/SocialGroupsList";
import { AddGroupDialog } from "@/components/social-groups/AddGroupDialog";
import { EditGroupDialog } from "@/components/social-groups/EditGroupDialog";
import { ViewGroupDialog } from "@/components/social-groups/ViewGroupDialog";
import { DeleteGroupDialog } from "@/components/social-groups/DeleteGroupDialog";
import { useSocialGroups } from "@/hooks/useSocialGroups";

const AdminSocialGroups = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  
  const {
    currentItems,
    searchTerm,
    setSearchTerm,
    currentPage,
    totalPages,
    setCurrentPage,
    editingGroup,
    isAddDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isViewDialogOpen,
    setIsAddDialogOpen,
    setIsEditDialogOpen,
    setIsDeleteDialogOpen,
    setIsViewDialogOpen,
    openAddDialog,
    openEditDialog,
    openDeleteDialog,
    openViewDialog,
    handleAddGroup,
    handleEditGroup,
    handleDeleteGroup
  } = useSocialGroups(i18n.language);

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <Button variant="ghost" onClick={() => navigate("/admin-dashboard")} className="flex items-center gap-2">
          <ArrowLeft size={16} />
          {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
        </Button>
        <h1 className="text-xl font-bold">
          {isRTL ? "إدارة مجموعات التواصل الاجتماعي" : "Social Media Groups Management"}
        </h1>
        <div className="w-[100px]"></div>
      </div>

      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl">
                {isRTL ? "مجموعات التواصل الاجتماعي" : "Social Media Groups"}
              </CardTitle>
              <Button onClick={openAddDialog} className="flex items-center gap-2">
                <Plus size={16} />
                {isRTL ? "إضافة مجموعة" : "Add Group"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <SearchInput
                placeholder={isRTL ? "البحث عن مجموعات..." : "Search groups..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="md:w-1/3"
              />
              
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {isRTL ? "إظهار" : "Showing"}{" "}
                <span className="font-medium text-foreground">
                  {currentItems.length > 0 ? 
                    `${Math.min(currentItems.length, ((currentPage - 1) * 5) + 1)} - ${Math.min(currentItems.length + ((currentPage - 1) * 5), currentPage * 5)}` : "0"
                  }
                </span>{" "}
                {isRTL ? "من" : "of"}{" "}
                <span className="font-medium text-foreground">{currentItems.length}</span>{" "}
                {isRTL ? "مجموعة" : "groups"}
              </div>
            </div>

            <SocialGroupsList
              currentItems={currentItems}
              isRTL={isRTL}
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              openViewDialog={openViewDialog}
              openEditDialog={openEditDialog}
              openDeleteDialog={openDeleteDialog}
            />
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <AddGroupDialog 
        isOpen={isAddDialogOpen}
        setIsOpen={setIsAddDialogOpen}
        onSubmit={handleAddGroup}
        isRTL={isRTL}
      />

      <EditGroupDialog
        isOpen={isEditDialogOpen}
        setIsOpen={setIsEditDialogOpen}
        onSubmit={handleEditGroup}
        group={editingGroup}
        isRTL={isRTL}
      />

      <ViewGroupDialog
        isOpen={isViewDialogOpen}
        setIsOpen={setIsViewDialogOpen}
        group={editingGroup}
        isRTL={isRTL}
      />

      <DeleteGroupDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        group={editingGroup}
        onDelete={handleDeleteGroup}
        isRTL={isRTL}
      />
    </AdminPageBackground>
  );
};

export default AdminSocialGroups;
